/* MotionGrey header search. Uses the theme's locale-aware Shopify search route. */
(function () {
  'use strict';
  if (window.motionGreySearchInitialized) return;
  window.motionGreySearchInitialized = true;

  var instances = new WeakMap();
  var activeSearch = null;
  var nextId = 0;
  var backdrop = null;
  var backdropMotion = null;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function fadeBackdrop(open) {
    var opacity = backdropMotion ? getComputedStyle(backdrop).opacity : (open ? 0 : 0.6);
    if (backdropMotion) backdropMotion.cancel();
    backdropMotion = null;
    if (reducedMotion.matches || !backdrop.animate) return;
    var motion = backdrop.animate([{ opacity: opacity }, { opacity: open ? 0.6 : 0 }], {
      duration: 250, easing: 'ease-in-out', fill: 'both'
    });
    backdropMotion = motion;
    motion.onfinish = function () {
      if (open && backdropMotion === motion) {
        motion.cancel();
        backdropMotion = null;
      }
    };
  }

  function safeUrl(value, fallback) {
    if (!value) return fallback;
    try {
      var url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : fallback;
    } catch (_) {
      return fallback;
    }
  }

  function Search(panel) {
    this.panel = panel;
    this.form = panel.querySelector('.mg-search__form');
    this.input = panel.querySelector('.mg-search__input');
    this.clearButton = panel.querySelector('.mg-search__clear');
    this.results = panel.querySelector('.mg-search__results');
    this.status = panel.querySelector('.mg-search__status');
    this.controller = null;
    this.timer = null;
    this.version = 0;
    this.trigger = null;
    this.motion = null;
    this.closing = false;
    this.panel.id = 'mg-search-dialog-' + (++nextId);
    this.panel.parentElement.classList.add('mg-search-host');

    var self = this;
    this.panel.parentElement.querySelectorAll('[data-search-mobile-toggle]').forEach(function (trigger) {
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Search products');
    });

    // The legacy theme has a document-level click-to-dismiss listener.
    panel.addEventListener('click', function (event) { event.stopPropagation(); });
    panel.addEventListener('keydown', function (event) { self.onKeydown(event); });
    panel.addEventListener('focusin', function (event) {
      var card = event.target.closest('.mg-search__card');
      // Keep keyboard focus visible without moving a card during a pointer click.
      if (card && card.matches(':focus-visible')) {
        card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
      }
    });
    panel.querySelector('[data-search-close]').addEventListener('click', function () { self.close(); });
    this.input.addEventListener('input', function (event) {
      if (!event.isComposing) self.queue();
    });
    this.input.addEventListener('compositionstart', function () { self.cancel(); });
    this.input.addEventListener('compositionend', function () { self.queue(); });
    this.clearButton.addEventListener('click', function () {
      self.input.value = '';
      self.queue();
      self.input.focus();
    });
    this.form.addEventListener('submit', function (event) {
      self.input.value = self.input.value.trim();
      if (!self.input.value) {
        event.preventDefault();
        self.queue();
        self.input.focus();
      }
    });
  }

  Search.prototype.open = function (trigger) {
    if (activeSearch === this && !this.closing) {
      this.input.focus({ preventScroll: true });
      return;
    }
    if (activeSearch && activeSearch !== this) activeSearch.close(false, true);
    var height = this.panel.hidden ? 0 : this.panel.getBoundingClientRect().height;
    var opacity = this.panel.hidden ? 0 : getComputedStyle(this.panel).opacity;
    this.stopMotion();
    this.closing = false;
    activeSearch = this;
    this.trigger = trigger;
    this.panel.hidden = false;
    this.panel.inert = false;
    this.panel.removeAttribute('aria-hidden');
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('search-open', 'mg-search-active');
    this.fit();
    if (activeSearch !== this) return;
    this.queue(true);
    fadeBackdrop(true);
    this.transition(height, opacity);
    this.input.focus({ preventScroll: true });
  };

  Search.prototype.stopMotion = function () {
    if (this.motion) this.motion.cancel();
    this.motion = null;
    this.panel.classList.remove('mg-search--animating');
  };

  Search.prototype.transition = function (height, opacity, complete) {
    this.stopMotion();
    if (reducedMotion.matches || !this.panel.animate) {
      if (complete) complete();
      return;
    }
    var targetHeight = this.closing ? 0 : this.panel.getBoundingClientRect().height;
    this.panel.classList.add('mg-search--animating');
    var motion = this.panel.animate([
      { height: height + 'px', opacity: opacity },
      { height: targetHeight + 'px', opacity: this.closing ? 0 : 1 }
    ], { duration: 250, easing: 'ease-in-out', fill: 'both' });
    this.motion = motion;
    var self = this;
    motion.onfinish = function () {
      if (self.motion !== motion) return;
      self.stopMotion();
      if (complete) complete();
    };
  };

  Search.prototype.replaceResults = function (content, visible, skipMotion) {
    var height = this.panel.getBoundingClientRect().height;
    var opacity = getComputedStyle(this.panel).opacity;
    this.stopMotion();
    this.results.replaceChildren(content);
    this.results.hidden = !visible;
    if (!skipMotion && !this.closing) this.transition(height, opacity);
  };

  Search.prototype.fit = function () {
    if (!this.panel.getClientRects().length) {
      this.close(false, true);
      return;
    }
    var height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    this.panel.style.setProperty('--mg-search-max-height', Math.max(0, height - this.panel.getBoundingClientRect().top) + 'px');
  };

  Search.prototype.cancel = function () {
    this.version += 1;
    window.clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
    this.controller = null;
    this.results.removeAttribute('aria-busy');
  };

  Search.prototype.close = function (restoreFocus, immediate) {
    if (this.panel.hidden || (this.closing && !immediate)) return;
    var height = this.panel.getBoundingClientRect().height;
    var opacity = getComputedStyle(this.panel).opacity;
    this.cancel();
    this.closing = true;
    this.status.textContent = '';
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus !== false && this.trigger && this.trigger.getClientRects().length) {
      this.trigger.focus({ preventScroll: true });
    } else if (this.panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    this.panel.inert = true;
    this.panel.setAttribute('aria-hidden', 'true');
    var self = this;
    function finish() {
      self.stopMotion();
      self.panel.hidden = true;
      self.closing = false;
      if (activeSearch === self) {
        activeSearch = null;
        document.documentElement.classList.remove('search-open', 'mg-search-active');
        if (backdropMotion) backdropMotion.cancel();
        backdropMotion = null;
      }
    }
    if (immediate) { finish(); return; }
    fadeBackdrop(false);
    this.transition(height, opacity, finish);
  };

  Search.prototype.onKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowDown' && event.target === this.input) {
      var firstResult = this.panel.querySelector('.mg-search__results:not([hidden]) .mg-search__card');
      if (firstResult) {
        event.preventDefault();
        firstResult.focus();
      }
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])'))
      .filter(function (element) { return element.getClientRects().length && element.tabIndex >= 0; });
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  Search.prototype.searchUrl = function (query) {
    var url = new URL(this.form.action, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    return url;
  };

  Search.prototype.queue = function (skipMotion) {
    // An IME composition event can arrive after blur has started dismissal.
    if (this.closing || this.panel.hidden) return;
    this.cancel();
    var query = this.input.value.trim();
    this.clearButton.hidden = !this.input.value;
    this.status.textContent = '';
    if (query.length < 2) {
      this.replaceResults(document.createDocumentFragment(), false, skipMotion);
      return;
    }

    var loading = document.createElement('p');
    loading.className = 'mg-search__loading';
    loading.textContent = 'Finding product matches…';
    this.replaceResults(loading, true, skipMotion);
    this.results.setAttribute('aria-busy', 'true');
    var self = this;
    var version = this.version;
    this.timer = window.setTimeout(function () { self.load(query, version); }, 250);
  };

  Search.prototype.message = function (query, error) {
    var fragment = this.panel.querySelector('[data-search-message]').content.cloneNode(true);
    fragment.querySelector('h3').textContent = error ? 'Suggestions are taking a little longer.' : 'No matches for “' + query + '”';
    fragment.querySelector('p').textContent = error
      ? 'You can still search the full store below.'
      : 'Try “desk”, “chair” or a different product name.';
    var link = fragment.querySelector('a');
    link.hidden = !error;
    link.href = this.searchUrl(query).href;
    this.replaceResults(fragment, true);
    this.status.textContent = error ? 'Suggestions unavailable. Search all products instead.' : 'No products found for ' + query + '.';
  };

  Search.prototype.render = function (data, query) {
    if (!data || !Array.isArray(data.results)) throw new Error('Invalid search response');
    if (!data.results.length) {
      this.message(query, false);
      return;
    }
    var fragment = this.panel.querySelector('[data-search-results]').content.cloneNode(true);
    var list = fragment.querySelector('.mg-search__grid');
    var hasCount = data.results_count != null && Number.isFinite(Number(data.results_count));
    var count = hasCount ? Math.max(Number(data.results_count), data.results.length) : null;
    var all = fragment.querySelector('.mg-search__view-all');
    all.href = this.searchUrl(query).href;
    all.querySelector('span').textContent = hasCount
      ? 'View all ' + count + (count === 1 ? ' result' : ' results')
      : 'View all results';
    var self = this;

    data.results.forEach(function (product) {
      var card = self.panel.querySelector('[data-search-card]').content.cloneNode(true);
      card.querySelector('a').href = safeUrl(product.url, self.searchUrl(query).href);
      card.querySelector('.mg-search__product-title').textContent = product.title || '';
      card.querySelector('.mg-search__price > span').textContent = (product.price_varies ? 'From ' : '') + (product.price || '');
      var previousPrice = card.querySelector('s');
      previousPrice.hidden = !product.on_sale;
      previousPrice.textContent = product.compare_at_price || '';
      var image = card.querySelector('img');
      var imageUrl = safeUrl(product.image, null);
      if (imageUrl) {
        image.src = imageUrl;
        image.alt = product.image_alt || product.title || '';
      } else {
        image.remove();
      }
      list.appendChild(card);
    });
    this.replaceResults(fragment, true);
    this.status.textContent = hasCount
      ? 'Showing ' + data.results.length + ' of ' + count + ' products for ' + query + '.'
      : 'Showing ' + data.results.length + ' product suggestions for ' + query + '.';
  };

  Search.prototype.load = async function (query, version) {
    if (activeSearch !== this || this.version !== version) return;
    var controller = new AbortController();
    this.controller = controller;
    var timeout = window.setTimeout(function () { controller.abort(); }, 8000);
    try {
      var url = this.searchUrl(query + '*');
      url.searchParams.set('view', 'json');
      var response = await fetch(url.href, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable');
      var data = await response.json();
      if (this.version === version && activeSearch === this) this.render(data, query);
    } catch (_) {
      if (this.version === version && activeSearch === this) this.message(query, true);
    } finally {
      window.clearTimeout(timeout);
      if (this.version === version) {
        this.controller = null;
        this.results.removeAttribute('aria-busy');
      }
    }
  };

  function init() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'mg-search-backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      document.body.appendChild(backdrop);
    }
    document.querySelectorAll('[data-mg-search]').forEach(function (panel) {
      if (!instances.has(panel)) instances.set(panel, new Search(panel));
    });
  }

  // Capture only search triggers before the theme's legacy delegated handlers.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-search-mobile-toggle]');
    if (trigger) {
      var panel = trigger.parentElement.querySelector('[data-mg-search]');
      if (!panel) return;
      init();
      event.preventDefault();
      event.stopImmediatePropagation();
      instances.get(panel).open(trigger);
      return;
    }
    if (activeSearch && !activeSearch.panel.contains(event.target)) activeSearch.close(false);
  }, true);

  window.addEventListener('resize', function () { if (activeSearch) activeSearch.fit(); });
  if (window.visualViewport) window.visualViewport.addEventListener('resize', function () { if (activeSearch) activeSearch.fit(); });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    if (activeSearch && event.target.contains(activeSearch.panel)) activeSearch.close(false, true);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}());
